<template>
  <v-card class="questionnaire-body-editor my-5" :class="{fullScreen: fullScreen}">
    <!-- Modals -->
    <AdArtefactSelection
      v-if="artefactSelectionModal.tree"
      v-model="artefactSelectionModal.open"
      :tree="artefactSelectionModal.tree"
      :exclude="artefactSelectionModal.selected"
      :versionProperty="artefactSelectionModal.versionProp"
      :treeFilters="artefactSelectionModal.filters"
      :multiple="artefactSelectionModal.multiple"
      :treeSelectOnOpen="artefactSelectionModal.treeSelectOnOpen"
      :addButtonType="artefactSelectionModal.addButtonType"
      :noDynamicVersions="artefactSelectionModal.disableDynamicVersions"
      :title="artefactSelectionModal.title ? artefactSelectionModal.title : undefined"
      @selected="artefactSelectionModal.selectHandler"
      @unselect="artefactSelectionModal.unselectHandler"
      @close="resetArtefactSelectionModal"
    >
      <template #title:append v-if="artefactSelectionModal.titleAppend.includes('createMenu')">
        <v-spacer />
        <v-menu offset-y>
          <template #activator="{ on, attrs }">
            <v-btn v-if="valueSetsAreEditable" v-bind="attrs" v-on="on" color="grey" small dark icon outlined>
              <v-icon> mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list>
            <AdButton type="inherit"
              :text="$t('new-valueset-ref')"
              :hint="$t('new-valueset-ref')"
              list-item
              @click="modals.valuesetNewReference.open = true"
            />
            <AdButton
              type="add"
              :text="$t('add-valueset')"
              :hint="$t('add-valueset')"
              list-item
              @click="startAddNewValueset"
            />
            <AdButton
              type="clone"
              :text="$t('clone-valueset')"
              :hint="$t('clone-valueset')"
              list-item
              @click="modals.valuesetClone.open = true"
            />
          </v-list>
        </v-menu>
      </template>

      <template #details.content:append="{ selectedVersion }"
        v-if="artefactSelectionModal.detailsAppend.includes('strength')">
        <v-autocomplete v-if="!selectedVersion?.disabled" v-model="selectedVersion.strength"
          :label="$t('coding-strength')" :items="getCodingStrengthOptions(selectedVersion)" clearable />
      </template>
    </AdArtefactSelection>

    <ValueSetClone :showDialog="modals.valuesetClone.open" :autoSelect="false" @cancel="modals.valuesetClone.open = false"/>
    <ValueSetAddReference v-model="modals.valuesetNewReference.open" :autoSelect="false" />

    <!-- The EditUnitOptions modal is not only used for item.unitOption, but also for itemControl and code! -->
    <EditUnitOptions v-model="modals.editUnitOptions.open" :selected="modals.editUnitOptions.selected"
      @add="addUnitOption" :title="editUnitOptionsTitle" :codeSystem="modals.editUnitOptions.target === 'code'"
      :canonicalUriRequired="modals.editUnitOptions.target !== 'code'" />

    <!-- Items title bar for full screen mode -->
    <AdCardTitle v-if="fullScreen">
      <div class="d-flex">
        <AdStatusDot darker tooltip larger :status="questionnaireActive.statusCode"
          :icon="$filters.getQuestionnaireIcon(questionnaireActive)" />
        <span class="pl-2">{{ $filters.getMatchingTranslation({ value: questionnaireActive.name }) }}</span>
        <AdRefChip v-if="questionnaireActive.ref && questionnaireActive.ident" class="d-inline mt-1"
          :ident="questionnaireActive.ident" darker small />
        <span class="ml-2 text--disabled">({{ $t('items') }})</span>
      </div>
    </AdCardTitle>
    <!-- Items title bar for regular mode -->
    <div v-else class="d-flex align-center">
      <AdCardTitle inline>{{ $t('items') }}</AdCardTitle>
    </div>

    <v-card-text>
      <TreeTable @selected="handleSelectTreeItem" :items="editableContent" :headers="tableHeaders" item-key="linkId"
        item-children="item" itemText="text" :loading="isLoading('questionnaireItem')" selectMode
        :tableHeight="fullScreen ? '100%' : '600px'">
        <template #buttons>
          <div v-if="questionnaireItemIsEditable">
            <AdButton v-if="!editMode" type="edit" @click="startEditMode" />
            <template v-else>
              <AdButton type="save" @click="saveQuestionnaire" />
              <AdButton type="cancel" @click="cancelEditMode(true)" />
            </template>
            <AdButton v-if="fullScreen" type="exit-fullscreen" list @click="fullScreen = false" />
            <AdButton v-if="!fullScreen" type="fullscreen" list @click="fullScreen = true" />
          </div>
        </template>

        <template #item.text="{ item }">
          <v-icon class="pr-2" :color="$filters.getStatusColor(item.statusCode)">
            {{ itemIcon(item) }}
          </v-icon>
          <v-tooltip top :open-on-click="false" :open-on-focus="false" open-delay="1200" nudge-bottom="15"
            color="rgba(0,0,0, 0.9)"
            :disabled="htmlToText($filters.getMatchingTranslation({ value: item.text })).length < 55" max-width="500px">
            <template #activator="{ on }">
              <span v-on="on">{{ tructateText(htmlToText($filters.getMatchingTranslation({ value: item.text })),
                tableTextTruncation) }}</span>
            </template>
            {{ htmlToText($filters.getMatchingTranslation({ value: item.text })) }}
          </v-tooltip>
        </template>

        <template #item.required="{ item }">
          {{ itemCardinality(item) }}
        </template>

        <template #item.actions="{ item }">
          <v-menu offset-y>
            <template #activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" icon x-small @click.stop.prevent="">
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </template>

            <v-list dense>
              <template v-for="(insertPosition, key) of insertPositions">
                <v-list-item v-if="!!insertPosition.condition && insertPosition.condition(item)"
                  @click="onInsert({...insertPosition, target: item})" :key="key">
                  <v-list-item-content>
                    <v-list-item-title>{{ $t(insertPosition.text) }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
              <v-list-item v-if="itemIsDeletable(item)" style="color: red" @click="deleteItem(item)">
                <v-list-item-content>
                  <v-list-item-title>{{ $t('delete') }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>

          </v-menu>
        </template>

        <template #active-item>
          <AdInfoOwl v-if="!selectedItem">{{ $t('select-item') }}</AdInfoOwl>
          <v-card v-else class="ma-1">
            <div class="cardContent" style="position: relative">
              <AdCardTitle style="position: sticky; top: 0; z-index: 3;">
                <span>{{ tructateText(htmlToText($filters.getMatchingTranslation({ value: selectedItem.text })))
                  }}</span>
                <template v-if="editMode">
                  <v-spacer />
                  <AdButton list type="moveConceptDown" @click="moveSelectedItem('down')"
                    :disabled="selectedItemIsLastChild" />
                  <AdButton list type="moveConceptUp" @click="moveSelectedItem('up')"
                    :disabled="selectedItemIsFirstChild" />
                </template>
              </AdCardTitle>

              <v-card-text class="py-5">
                <!-- LinkId and type -->
                <v-row dense>
                  <v-col>
                    <EditableString readonly :value="selectedItem.linkId" :label="$t('id')" />
                  </v-col>
                  <v-col>
                    <EditableQuestionnaireItemType v-model="selectedItem.type" @input="cleanupAfterTypeChange"
                      :readonly="selectedItem.type === 'group' || !editMode" :label="$t('type')" />
                  </v-col>
                </v-row>

                <!-- Text -->
                <v-row dense>
                  <v-col>
                    <EditableTranslationEditor v-model="selectedItem.text" :label="$t('text')" :locales="projectLocales"
                      editor :minHeight="Number(20)" :readonly="!editMode" />
                  </v-col>
                </v-row>

                <!-- possible synonym-->
                <v-row v-if="selectedItem.synonym" dense class="pb-3">
                  <v-col>
                    {{ $t('synonym') }}:
                    {{ htmlToText($filters.getMatchingTranslation({ value: selectedItem.synonym })) }}
                  </v-col>
                </v-row>

                <!-- possible help text-->
                <v-row v-if="selectedItem.operationalization" dense class="pb-3">
                  <v-col>
                    {{ $t('help-text') }}:
                    <span v-html="$filters.getMatchingTranslation({ value: selectedItem.operationalization })"></span>
                  </v-col>
                </v-row>

                <!-- Prefix, Definition -->
                <v-row v-if="selectedItem.prefix || selectedItem.definition || editMode" dense>
                  <v-col v-if="selectedItem.prefix || editMode" cols="auto">
                    <EditableString v-model="selectedItem.prefix" :label="$t('prefix')" :readonly="!editMode" />
                  </v-col>
                  <v-col v-if="selectedItem.definition || editMode">
                    <EditableString v-model="selectedItem.definition" :label="$t('definition')" :readonly="!editMode" />
                  </v-col>
                </v-row>

                <!-- Cardinality switches and summary -->
                <v-row dense class="mt-3">
                  <v-col cols="auto">
                    <EditableSwitch v-model="selectedItem.repeats" :label="$t('repeats')" :readonly="!editMode" />
                  </v-col>
                  <v-col cols="auto" class="mr-2">
                    <EditableSwitch v-model="selectedItem.required" :label="$t('required')" :readonly="!editMode" class="cap-label"/>
                  </v-col>
                  <v-col cols="3" style="font-size: 16px" class="font-italic text--disabled">
                    <span class="font-weight-bold">{{ $t('cardinality')}} </span>
                    <span>{{ itemCardinality(selectedItem) }}</span>
                  </v-col>
                  <v-col cols="auto" class="mr-1">
                    <EditableSwitch
                      v-model="selectedItem.readOnly"
                      :label="$t('readonly')"
                      :readonly="!editMode"
                      :disabled="readOnlySwitchIsDisabled"
                      @change="handleReadOnlyChange('readOnly', $event)"
                    />
                  </v-col>
                  <v-col cols="auto" class="mr-1">
                    <EditableSwitch
                      v-model="selectedItem.hiddenItem"
                      :label="$t('hidden')"
                      :readonly="!editMode"
                      :disabled="hiddenItemSwitchIsDisabled"
                      @change="handleReadOnlyChange('hiddenItem', $event)"
                    />
                  </v-col>
                </v-row>

                <!-- EnableWhen -->
                <v-row dense>
                  <v-col>
                    <StructuralConditionEditor v-if="editMode" :conditions="selectedItem.enableWhen"
                      :representingTemplate="questionnaireContent" mode="questionnaire" cardClasses="px-0 py-1"
                      :artefactSelectionItemText="(item) => tructateText(htmlToText($filters.getMatchingTranslation({ value: item.text })))"
                      @conditionsUpdated="handleConditionUpdated">
                      <template #condition-table-top>
                        <AdCardTitle inline>{{ $t('conditions') }}</AdCardTitle>
                        <v-radio-group v-model="selectedItem.enableBehavior" hide-details row :dense="!fullScreen"
                          mandatory :label="$t('enable-behavior')" class="mt-0 ml-4 mb-2">
                          <v-radio v-for="behavior in enableBehaviorTypes" :key="behavior.value" :label="behavior.value"
                            :value="behavior.value" />
                        </v-radio-group>
                      </template>
                    </StructuralConditionEditor>
                    <AdConditional v-else-if="selectedItem.enableWhen && selectedItem.enableWhen.length"
                      :enableWhen="selectedItem.enableWhen" :getQuestionName="getQuestionName"
                      :title="enableWhenTitle" />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col v-if="showRenderingStyle">
                    <EditableString v-model="selectedItem.renderingStyle" :label="$t('rendering-style')"
                      :readonly="!editMode" />
                  </v-col>
                </v-row>

                <!-- minValue / maxValue -->
                <v-row v-if="showMinMaxValueCard">
                  <v-col v-if="showMinValueInput" cols="auto">
                    <v-component :is="minMaxValueComponent(selectedItem)" v-model="selectedItem.minValue"
                      :label="$t('minimum')" :readonly="!editMode" :required="false" />
                  </v-col>
                  <v-col v-if="showMaxValueInput" cols="auto">
                    <v-component :is="minMaxValueComponent(selectedItem)" v-model="selectedItem.maxValue"
                      :label="$t('maximum')" :readonly="!editMode" :required="false" />
                  </v-col>
                </v-row>

                <!-- Unit option -->
                <v-card class="my-5" outlined v-if="showUnitOptionCard">
                  <AdCardTitle inline>{{ $t('unit-option') }}
                    <template v-if="editMode">
                      <v-spacer></v-spacer>
                      <AdButton type="add" @click="startEditUnitOption(null, 'unitOption')" />
                    </template>
                  </AdCardTitle>
                  <v-card-text class="pa-0">
                    <v-data-table :items="selectedItem.unitOption" :headers="unitOptionHeaders" item-key="code" dense
                      disable-pagination disable-sort disable-filtering hide-default-footer>
                      <template #item.actions="{ item }">
                        <AdSpeedDial direction="left">
                          <AdSpeedOption type="edit" @click="startEditUnitOption(item, 'unitOption')" />
                          <AdSpeedOption type="delete" @click="removeUnitOption(item, 'unitOption')" />
                        </AdSpeedDial>
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>

                <!-- Code -->
                <v-card class="my-5" outlined v-if="showCodeCard">
                  <AdCardTitle inline>{{ $t('code') }} (Item)
                    <template v-if="editMode">
                      <v-spacer></v-spacer>
                      <AdButton type="add" @click="startEditUnitOption(null, 'code')" />
                      <AdButton type="add" :text="$t('search')" :hint="$t('terminology-browser')"
                        @click="startTerminologyBrowser()" />
                    </template>
                  </AdCardTitle>
                  <v-card-text class="pa-0">
                    <v-data-table v-if="codesAvailable" :items="selectedItem.code" :headers="codeHeaders"
                      item-key="code" dense disable-pagination disable-sort disable-filtering hide-default-footer>
                      <template #item.codeSystem="{ item }">
                        <div>{{ item.codeSystemName ||
                          $store.getters['terminology/getCodeSystemName'](item.codeSystem)
                          }}</div>
                      </template>
                      <template #item.actions="{ item }">
                        <AdSpeedDial direction="left">
                          <AdSpeedOption type="edit" @click="startEditUnitOption(item, 'code')" />
                          <AdSpeedOption type="delete" @click="removeUnitOption(item, 'code')" />
                        </AdSpeedDial>
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>

                <!-- ItemControl -->
                <v-card class="my-5" outlined v-if="showItemControlCard">
                  <AdCardTitle inline>{{ $t('item-control') }}
                    <template v-if="editMode">
                      <v-spacer></v-spacer>
                      <AdButton type="add" @click="startEditUnitOption(null, 'itemControl')" />
                    </template>
                  </AdCardTitle>
                  <v-card-text class="pa-0">
                    <v-data-table v-if="itemControlsAvailable" :items="selectedItem.itemControl"
                      :headers="itemControlHeaders" item-key="code" dense disable-pagination disable-sort
                      disable-filtering hide-default-footer>
                      <template #item.actions="{ item }">
                        <AdSpeedDial direction="left">
                          <AdSpeedOption type="edit" @click="startEditUnitOption(item, 'itemControl')" />
                          <AdSpeedOption type="delete" @click="removeUnitOption(item, 'itemControl')" />
                        </AdSpeedDial>
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>

                <v-card v-if="showAnswerOptionsCard" class="my-5" outlined>
                  <AdCardTitle inline>
                    {{ $t('answer-options') }}
                    <v-icon color="grey lighten-1" class="pl-7">{{ answerOptionsListTypeIcon }}</v-icon>
                    <template v-if="editMode">
                      <div class="answer-options-info-btn"><EditableInfoIcon :infoText="$t('answer-options-hint-editmode')"/></div>
                      <v-spacer></v-spacer>
                      <AdButton type="add" disabled @click="()=>{}" />
                    </template>
                  </AdCardTitle>
                  <v-card-text class="pa-0">
                    <v-data-table
                      v-if="answerOptionsAvailable"
                      :items="answerOptionsList"
                      :headers="answerOptionsHeaders"
                      item-key="uuid"
                      dense
                      disable-pagination
                      disable-sort
                      disable-filtering
                      hide-default-footer
                    >
                      <template #item.value="{ item }">
                        <span v-if="item.valueString">
                          {{ $filters.getMatchingTranslation({value: item.valueString, selector: 'value'}) }}
                        </span>
                        <span v-else-if="item.valueDate">
                          <!-- use also getMatchingTranslation to properly display the value -->
                          {{ $filters.getMatchingTranslation({ value: item.valueDate, selector: 'value' }) }}
                        </span>
                        <span v-else-if="item.valueTime">
                          <!-- use also getMatchingTranslation to properly display the value -->
                          {{ $filters.getMatchingTranslation({ value: item.valueTime, selector: 'value' }) }}
                        </span>
                        <span v-else-if="item.valueInteger">
                          <!-- use also getMatchingTranslation to properly display the value -->
                          {{ $filters.getMatchingTranslation({ value: item.valueInteger, selector: 'value' }) }}
                        </span>
                      </template>

                      <template #item.actions="{ }">
                        <AdSpeedDial direction="left">
                          <AdSpeedOption type="edit" @click="() => {}" />
                          <AdSpeedOption type="delete" @click="() => {}" />
                        </AdSpeedDial>
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>

                <v-card v-if="showAnswerValueSetCard" class="my-5" outlined>
                  <AdCardTitle inline>
                    {{ $t('answer-valueset') }}
                    <template v-if="editMode">
                      <v-spacer></v-spacer>
                      <AdButton type="add" @click="startAddAnswerValueSet({showCreateMenu: true})" />
                    </template>
                  </AdCardTitle>

                  <v-card-text class="pa-0">
                    <v-data-table
                      v-if="answerValueSetAvailable"
                      :items="answerValueSetList"
                      :headers="answerValueSetHeaders"
                      item-key="uuid"
                      :hide-default-footer="answerValueSetList.length <= 5"
                      :footer-props="{ 'items-per-page-options': [5,10] }"
                    >
                      <template #item.name="{ item }">
                        <div class="text-no-wrap">
                          <AdStatusDot v-if="item.statusCode" :status="item.statusCode" class="pr-2"/>
                          <AdButton v-if="item.ref && item.flexibility" link :to="getAnswerValueSetRouteObject(item)">{{ item.displayName || item.name }}</AdButton>
                          <template v-else>{{ item.displayName || item.name }}</template>
                        </div>
                      </template>

                      <template #item.flexibility="{ item }">
                        <div v-if="item.flexibility">
                          {{ item.flexibility === 'dynamic' ? $t('dynamic') : $filters.formatDate(item.flexibility) }}
                        </div>
                      </template>

                      <template #item.strength="{ item }">{{ getCodingStrengthLabel(item.strength) }}</template>

                      <template #item.actions="{ item }">
                        <AdSpeedDial v-if="editMode && userIsEditor" direction="left">
                          <AdSpeedOption type="edit" @click="startAddAnswerValueSet({ editUuid: item.uuid, selectOnOpen: onOpenObject(item) })" />
                          <AdSpeedOption type="delete" @click="deleteAnswerValueSet(item)" />
                        </AdSpeedDial>
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>

              </v-card-text>
            </div>

          </v-card>
        </template>

      </TreeTable>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import { cloneDeep, uniqBy, isEqual } from 'lodash'
import LockService from '@/services/LockService'
import EditUnitOptions from '@/components/Rule/EditUnitOptions'
import StructuralConditionEditor from '@/components/Utilities/StructuralConditionEditor'
import AdArtefactSelection from '@/components/Utilities/AdArtefactSelection'
import AdConditional from '@/components/Utilities/AdConditional'
import ValueSetClone from '@/components/Terminology/ValueSetClone'
import ValueSetAddReference from '@/components/Terminology/ValueSetAddReference'

export default {
  name: 'QuestionnaireBodyEditor',
  components: {
    AdArtefactSelection,
    AdConditional,
    EditUnitOptions,
    StructuralConditionEditor,
    ValueSetClone,
    ValueSetAddReference
  },
  data () {
    return {
      artefactSelectionModal: {
        addButtonType: null,
        title: null,
        open: false,
        selected: null,
        disableDynamicVersions: false,
        tree: null,
        multiple: false,
        selectHandler: null,
        filters: [],
        titleAppend: [],
        detailsAppend: [],
        treeSelectOnOpen: null,
        items: []
      },
      editableContent: null,
      editMode: false,
      fullScreen: false,
      insertPositions: [
        {
          type: 'group',
          mode: 'into',
          text: 'insert-new-group-into',
          condition: (item) => ['group', 'choice'].includes(item.type)
        },
        {
          type: 'group',
          mode: 'preceding',
          text: 'insert-new-group-before',
          condition: (item) => true
        },
        {
          type: 'group',
          mode: 'following',
          text: 'insert-new-group-after',
          condition: (item) => true
        },
        {
          type: 'item',
          mode: 'into',
          text: 'insert-new-item-into',
          condition: (item) => ['group', 'choice'].includes(item.type)
        },
        {
          type: 'item',
          mode: 'preceding',
          text: 'insert-new-item-before',
          condition: (item) => true
        },
        {
          type: 'item',
          mode: 'following',
          text: 'insert-new-item-after',
          condition: (item) => true
        }
      ],
      modals: {
        editUnitOptions: {
          open: false,
          selected: null,
          target: null
        },
        valuesetClone: { open: false },
        valuesetNewReference: { open: false }
      },
      selectedItem: null
    }
  },
  computed: {
    ...mapGetters('questionnaire', ['questionnaireActive', 'questionnaireItem', 'questionnaireItemIsEditable', 'isLoading']),
    ...mapGetters('app', ['projectLanguage']),
    ...mapGetters({ valueSetsAreEditable: ['terminology/valueSetsAreEditable'] }),
    ...mapGetters('server', ['getItemStatuses']),

    editableContentParentMap () {
      return this.$filters.createChildParentMap({ list: this.editableContent, childrenKey: 'item', uniqueKey: 'linkId' })
    },
    parentIsReadOnly () {
      const selectedItemId = this.selectedItem?.linkId
      if (!selectedItemId) return null
      const parent = this.editableContentParentMap.get(selectedItemId)?.parentRef
      return parent?.readOnly === 'true'
    },
    parentIsHidden () {
      const selectedItemId = this.selectedItem?.linkId
      if (!selectedItemId) return null
      const parent = this.editableContentParentMap.get(selectedItemId)?.parentRef
      return parent?.hiddenItem === 'true'
    },
    readOnlySwitchIsDisabled () {
      return !!this.parentIsReadOnly
    },
    hiddenItemSwitchIsDisabled () {
      return !!this.parentIsHidden
    },

    selectedItemIsFirstChild () {
      const parent = this.getParentItem(this.selectedItem)
      const childList = parent?.item || this.editableContent
      const index = childList.indexOf(this.selectedItem)
      return index === 0
    },
    selectedItemIsLastChild () {
      const parent = this.getParentItem(this.selectedItem)
      const childList = parent?.item || this.editableContent
      const index = childList.indexOf(this.selectedItem)
      return index === childList.length - 1
    },

    answerOptionsHeaders () {
      const headers = []
      if (this.answerOptionsList.some(option => ['valueString', 'valueDate', 'valueTime', 'valueInteger'].some(type => option[type]))) {
        headers.push({ value: 'value', text: this.$t('value') })
      }
      if (this.answerOptionsList.some(option => option.valueCoding)) {
        headers.push(
          { value: 'valueCoding.0.code', text: this.$t('code') },
          { value: 'valueCoding.0.displayName', text: this.$t('display-name') },
          { value: 'valueCoding.0.canonicalUri', text: this.$t('canonicalUri') },
          { value: 'valueCoding.0.codeSystem', text: this.$t('codesystem') }
        )
      }
      return headers
    },
    answerOptionsList () {
      if (!this.answerOptionsAvailable) return []
      return this.selectedItem.answerOption.map(option => {
        return {
          uuid: this.getUuid(),
          ...option
        }
      })
    },
    answerOptionsListTypeIcon () {
      if (!this.answerOptionsAvailable) return ''
      if (this.selectedItem.answerOption[0].valueCoding) return this.$filters.getValueDomainTypeIcon('code')
      if (this.selectedItem.answerOption[0].valueInteger) return this.$filters.getValueDomainTypeIcon('integer')
      if (this.selectedItem.answerOption[0].valueDate) return this.$filters.getValueDomainTypeIcon('datetime')
      if (this.selectedItem.answerOption[0].valueTime) return this.$filters.getValueDomainTypeIcon('time')
      if (this.selectedItem.answerOption[0].valueString) return this.$filters.getValueDomainTypeIcon('string')
      return ''
    },
    answerValueSetHeaders () {
      const headers = [
        { value: 'name', text: this.$t('name') },
        { value: 'flexibility', text: this.$t('flexibility') },
        { value: 'strength', text: this.$t('coding-strength') }
      ]
      if (this.fullScreen) {
        headers.splice(1, 0, { value: 'ref', text: this.$t('id') })
      }
      if (this.editMode && this.userIsEditor) {
        headers.push({ value: 'actions', align: 'right' })
      }
      return headers
    },
    answerValueSetList () {
      if (!this.answerValueSetAvailable) return []
      return this.selectedItem.answerValueSet.map(option => {
        return {
          uuid: this.getUuid(),
          ...option
        }
      })
    },
    codeHeaders () {
      return [
        { value: 'code', text: this.$t('code') },
        { value: 'displayName', text: this.$t('display-name') },
        { value: 'canonicalUri', text: this.$t('canonicalUri') },
        { value: 'codeSystem', text: this.$t('codesystem') },
        { value: 'actions', align: 'right' }
      ].filter(header => (this.editMode || header.value !== 'actions') && (this.fullScreen || header.value !== 'canonicalUri'))
    },
    codingStrengthOptions () {
      return this.$store.getters['server/getDecorTypes']?.data?.CodingStrengthType?.[0]?.enumeration?.map(cst => {
        return {
          text: this.$filters.getMatchingTranslation({ value: cst.label, locale: this.$i18n.locale }),
          value: cst.value,
          disabled: ['CNE', 'CWE'].includes(cst.value),
          hint: this.$filters.getMatchingTranslation({ value: cst.hint, locale: this.$i18n.locale })
        }
      }) || []
    },
    editUnitOptionsTitle () {
      const tagStart = this.modals.editUnitOptions.selected ? 'edit' : 'add'
      const target = this.modals.editUnitOptions.target
      if (!target) {
        return this.$t(tagStart)
      }
      // to kebab-case for conventional use of i18n tags
      const tagEnd = target.replace(/[A-Z]+(?![a-z])|[A-Z]/g, (match, offset) => (offset ? '-' : '') + match.toLowerCase())
      return this.$t(`${tagStart}-${tagEnd}`)
    },
    enableWhenTitle () {
      let title = this.$t('conditions')
      if (this.selectedItem.enableBehavior) {
        title += ` (${this.$t(this.selectedItem.enableBehavior)})`
      }
      return title
    },
    enableBehaviorTypes () {
      return this.$store.getters['server/getDecorTypes']?.data?.EnableBehaviorType?.[0]?.enumeration || []
    },
    getCodingStrengthLabel () {
      return strength => {
        const match = this.$store.getters['server/getDecorTypes']?.data?.CodingStrengthType?.[0]?.enumeration?.find((s) => s.value === strength)
        return match
          ? this.$filters.getMatchingTranslation({ value: match.label, locale: this.$i18n.locale })
          : strength
      }
    },
    getQuestionName () {
      return id => {
        const item = this.$filters.listHelper.findItemInList({
          items: this.questionnaireContent,
          children: 'item',
          handler: match => match.linkId === id
        })
        if (Array.isArray(item?.text)) return this.$filters.getMatchingTranslation({ value: item.text })
        return id
      }
    },
    tableHeaders () {
      return [
        {
          text: this.$t('items'),
          value: 'text',
          width: '50%'
        },
        {
          text: this.$t('Card'),
          value: 'required',
          width: '5%'
        },
        {
          value: 'actions',
          width: '5%',
          align: 'right'
        }
      ].filter(header => this.editMode || header.value !== 'actions')
    },
    hasUnsavedChanges () {
      return !isEqual(this.editableContent, this.questionnaireContent)
    },
    itemCardinality () {
      return item => {
        const rq = (item.required === 'true') ? '1' : '0'
        const rp = (item.repeats === 'true') ? '*' : '1'
        return rq + '...' + rp
      }
    },
    itemControlHeaders () {
      return [
        { value: 'code', text: this.$t('code') },
        { value: 'displayName', text: this.$t('display-name') },
        { value: 'canonicalUri', text: this.$t('canonicalUri') },
        { value: 'actions', align: 'right' }
      ].filter(header => this.editMode || header.value !== 'actions')
    },
    itemIcon () {
      return item => {
        if (item.type === 'group') return 'mdi-folder'
        if (item.type === 'display') return 'mdi-tag-text-outline'
        return this.$filters.getValueDomainTypeIcon(item.type)
      }
    },
    itemIsDeletable () {
      return item => {
        const itemIsPersisted = !!this.$filters.listHelper.findItemInList({
          items: this.questionnaireContent,
          children: 'item',
          handler: match => match.linkId === item.linkId
        })
        return !itemIsPersisted
      }
    },
    minMaxValueComponent () {
      return item => {
        if (['integer'].includes(item.type)) return 'EditableCount'
        if (['decimal'].includes(item.type)) return 'EditableDecimal'
        if (['dateTime'].includes(item.type)) return 'EditableDateTime'
        if (['date'].includes(item.type)) return 'EditableDate'
        if (['time'].includes(item.type)) return 'EditableTime'
        return 'EditableCount'
      }
    },
    questionnaireContent () {
      return this.questionnaireItem?.item || []
    },
    showRenderingStyle () {
      return this.selectedItem.renderingStyle || this.editMode
    },
    codesAvailable () {
      return (Array.isArray(this.selectedItem.code) && this.selectedItem.code.length) || false
    },
    showCodeCard () {
      return this.codesAvailable || this.editMode
    },
    itemControlsAvailable () {
      return (Array.isArray(this.selectedItem.itemControl) && this.selectedItem.itemControl.length) || false
    },
    answerOptionsAvailable () {
      return (Array.isArray(this.selectedItem.answerOption) && this.selectedItem.answerOption.length) || false
    },
    answerValueSetAvailable () {
      return (Array.isArray(this.selectedItem.answerValueSet) && this.selectedItem.answerValueSet.length) || false
    },
    showItemControlCard () {
      return this.itemControlsAvailable || this.editMode
    },
    showAnswerOptionsCard () {
      return (!this.showAnswerValueSetCard || this.editMode) && !!(this.answerOptionsAvailable || this.editMode)
    },
    showAnswerValueSetCard () {
      return !!(this.answerValueSetAvailable || this.editMode)
    },
    showMinMaxValueCard () {
      return ['date', 'dateTime', 'time', 'decimal', 'integer'].includes(this.selectedItem.type) && (
        (this.selectedItem.minValue || this.selectedItem.maxValue) || this.editMode
      )
    },
    showMinValueInput () {
      return this.showMinMaxValueCard && (this.selectedItem.minValue || this.editMode)
    },
    showMaxValueInput () {
      return this.showMinMaxValueCard && (this.selectedItem.maxValue || this.editMode)
    },
    treeValuesets () {
      return {
        artefact: 'VS',
        name: this.$t('valuesets'),
        icon: 'mdi-heart',
        loading: this.$store.state.terminology.loading.valueSet,
        loaded: this.$store.state.terminology.loaded.valueSet,
        menu: false,
        insertable: false,
        editable: false,
        items: this.artefactSelectionModal.items,
        key: 'uuid',
        text: (item) => item.versionLabel ? `${item.displayName} (${item.versionLabel})` : item.displayName,
        children: null
      }
    },
    unitOptionsAvailable () {
      return (Array.isArray(this.selectedItem.unitOption) && this.selectedItem.unitOption.length)
    },
    showUnitOptionCard () {
      return ['quantity', 'decimal', 'integer'].includes(this.selectedItem.type) && (
        this.unitOptionsAvailable || this.editMode
      )
    },
    tableTextTruncation () {
      if (this.fullScreen) {
        return 80
      }
      return this.editMode ? 45 : 50
    },
    unitOptionHeaders () {
      return [
        { value: 'code', text: this.$t('code') },
        { value: 'displayName', text: this.$t('display-name') },
        { value: 'canonicalUri', text: this.$t('canonicalUri') },
        { value: 'actions', align: 'right' }
      ].filter(header => this.editMode || header.value !== 'actions')
    }
  },
  methods: {
    // The EditUnitOptions modal is not only used for item.unitOption, but also for itemControl and code!
    addUnitOption (unitOption) {
      const { target } = this.modals.editUnitOptions
      if (!Array.isArray(this.selectedItem[target])) {
        this.$set(this.selectedItem, target, [])
      }
      const optionIndex = this.selectedItem[target].findIndex(opt => opt.code === unitOption.code)
      if (optionIndex > -1) {
        this.selectedItem[target].splice(optionIndex, 1, unitOption)
      } else {
        this.selectedItem[target].push(unitOption)
      }
    },
    applyToChildren (item, cb) {
      if (Array.isArray(item.item)) {
        item.item.forEach(child => {
          cb(child)
          this.applyToChildren(child, cb)
        })
      }
    },
    async cancelEditMode (restore = false) {
      this.editMode = false
      this.fullScreen = false

      if (restore) {
        this.restoreEditableContent()
      }
      try {
        const objectId = this.questionnaireItem.id
        const effectiveDate = this.questionnaireItem.effectiveDate
        await LockService.unlockObjectId({ objectId, effectiveDate })
      } catch (error) {
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
        this.$oops(errorMessage)
      }
    },
    cleanupAfterTypeChange () {
      delete this.selectedItem.minValue
      delete this.selectedItem.maxValue
    },
    createNewItem (type = 'item') {
      if (type === 'item') {
        return {
          linkId: this.getUuid(),
          type: 'string',
          text: [
            {
              '#text': 'Question?',
              language: this.projectLanguage
            }
          ]
        }
      }
      if (type === 'group') {
        return {
          linkId: this.getUuid(),
          text: [
            {
              '#text': 'Group',
              language: this.projectLanguage
            }
          ],
          type: 'group',
          item: [
            {
              linkId: this.getUuid(),
              type: 'string'
            }
          ]
        }
      }
    },
    deleteAnswerValueSet (item) {
      if (!Array.isArray(this.selectedItem.answerValueSet)) return

      const list = cloneDeep(this.answerValueSetList)
      list.splice(list.findIndex(vs => vs.uuid === item.uuid), 1)
      list.forEach((vs, index) => {
        delete vs.uuid
      })
      this.selectedItem.answerValueSet = list
    },
    deleteItem (item) {
      this.editableContent = this.$filters.listHelper.removeItemFromList({
        items: this.editableContent,
        children: 'item',
        handler: match => match.linkId === item.linkId
      })

      if (this.selectedItem?.linkId === item.linkId) {
        this.selectedItem = null
      }
    },
    getAnswerValueSetRouteObject (item) {
      return {
        name: item.flexibility !== 'dynamic'
          ? 'terminology-value-set-version'
          : 'terminology-value-set',
        params: {
          id: item.ref,
          version: item.flexibility !== 'dynamic' ? item.flexibility : null
        }
      }
    },
    getCodingStrengthOptions (item) {
      // CWE and CNE are deprecated values. They should not be selectable.
      // They are only added to the list if the current strength is CWE or CNE (backward compatibility).
      const options = this.codingStrengthOptions
      if (item?.strength === 'CNE') return options.filter(option => option.value !== 'CWE')
      if (item?.strength === 'CWE') return options.filter(option => option.value !== 'CNE')
      return options.filter(option => option.value !== 'CNE' && option.value !== 'CWE')
    },
    /**
     * Get a reference to the parent item of a given item
     */
    getParentItem (item) {
      return this.$filters.listHelper.listHelper({
        items: this.editableContent,
        children: 'item',
        handler: match => match.item?.some(child => child.linkId === item.linkId)
      })?.item
    },
    getStrength (item) {
      const strength = this.answerValueSetList.find(answerVS => answerVS.ref === item.id && (
        answerVS.flexibility === (item.flexibility || item.effectiveDate)
      ))?.strength
      return strength || null
    },
    async handleReadOnlyChange (key, value) {
      if (this.selectedItem.type !== 'group') return
      if (!Array.isArray(this.selectedItem.item)) return
      if (value === 'false') {
        const agree = await this.$confirm(this.$t('confirm-apply-to-children'), {
          positive: this.$t('yes'),
          negative: this.$t('no')
        })
        if (!agree) return
      }

      this.applyToChildren(this.selectedItem, child => {
        child[key] = value
      })
    },
    htmlToText (htmlString) {
      const el = document.createElement('div')
      el.innerHTML = htmlString
      return el.textContent
    },
    insertNewItem ({ mode, target, item }) {
      if (mode === 'into') {
        target.item.push(item)
        return
      }

      if (!['preceding', 'following'].includes(mode)) {
        throw new Error('mode not supported')
      }

      const targetLinkId = target.linkId
      const helper = mode === 'preceding'
        ? 'addItemBeforeListItem'
        : 'addItemAfterListItem'
      this.editableContent = this.$filters.listHelper[helper]({
        items: this.editableContent,
        item,
        children: 'item',
        handler: match => match.linkId === targetLinkId
      })
    },
    moveSelectedItem (direction) {
      if (direction !== 'up' && direction !== 'down') {
        throw new Error('direction not supported')
      }
      const parent = this.getParentItem(this.selectedItem)
      const childList = parent?.item || this.editableContent
      const index = childList.indexOf(this.selectedItem)
      const newIndex = direction === 'up' ? index - 1 : index + 1
      childList.splice(newIndex, 0, childList.splice(index, 1)[0])
    },
    onInsert ({ type, target, mode }) {
      const newItem = this.createNewItem(type)
      return this.insertNewItem({ mode, target, item: newItem })
    },
    // Format object for ArtefactSelection component
    onOpenObject (item) {
      const onOpen = {
        tree: { id: item.ref },
        version: { [item.flexibility === 'dynamic' ? 'flexibility' : 'effectiveDate']: item.flexibility }
      }

      if (item.flexibility !== 'dynamic') {
        onOpen.version.flexibility = undefined // Make sure the correct version will be selected
      }
      return onOpen
    },
    handleConditionUpdated (conditions) {
      this.selectedItem.enableWhen = conditions
    },
    handleSelectTreeItem (item) {
      this.selectedItem = item
      // Seems to be needed when adding first enableWhen. Check behavior (also for scenarios)
      if (!Array.isArray(this.selectedItem.enableWhen)) {
        this.$set(this.selectedItem, 'enableWhen', [])
      }
    },
    addFirstCondition () {
      this.handleConditionUpdated([{}])
    },
    removeUnitOption (item, target = 'unitOption') {
      if (Array.isArray(this.selectedItem[target])) {
        this.selectedItem[target] = this.selectedItem[target].filter(option => option.code !== item.code)
      }
    },
    resetArtefactSelectionModal () {
      this.artefactSelectionModal.tree = null
      this.artefactSelectionModal.title = null
      this.artefactSelectionModal.selected = null
      this.artefactSelectionModal.multiple = false
      this.artefactSelectionModal.disableDynamicVersions = false
      this.artefactSelectionModal.versionProp = null
      this.artefactSelectionModal.selectHandler = null
      this.artefactSelectionModal.treeSelectOnOpen = null
      this.artefactSelectionModal.addButtonType = null
      this.artefactSelectionModal.titleAppend = []
      this.artefactSelectionModal.detailsAppendContent = []
    },
    prepareEditableContentForTreeview () {

    },
    restoreEditableContent () {
      this.editableContent = cloneDeep(this.questionnaireContent)
      this.restoreSelectedItem()
    },
    restoreSelectedItem () {
      if (this.selectedItem) {
        this.selectedItem = this.$filters.listHelper.listHelper({
          items: this.editableContent,
          children: 'item',
          handler: match => match.linkId === this.selectedItem.linkId
        }).item
      }
    },
    async saveQuestionnaire (closeWhenDone = true) {
      const data = {
        parameter: [
          {
            op: 'replace',
            path: '/item',
            value: {
              item: this.editableContent
            }
          }
        ]
      }
      try {
        await this.$store.dispatch('questionnaire/patchQuestionnaire', {
          item: this.questionnaireItem,
          data
        })
      } catch (error) {
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || error.message || 'Error'
        this.$oops(errorMessage)
        this.restoreEditableContent()
      } finally {
        if (closeWhenDone) {
          this.cancelEditMode()
        }
        this.restoreSelectedItem()
      }
    },
    async startAddAnswerValueSet ({ selectOnOpen, showCreateMenu, editUuid } = {}) {
      if (!this.$store.getters['terminology/isLoaded']('valueSet')) {
        try {
          await this.$store.dispatch('terminology/getValueSetList', { resolveit: true, sort: 'displayName' })
        } catch (error) {
          const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
          this.$oops(errorMessage)
        }
      }

      const parsedItems = this.$store.state.terminology.valueSet?.map(item => {
        const newItem = { ...item }
        const strength = this.getStrength({ ...item, flexibility: 'dynamic' })
        newItem.valueSet = item.valueSet?.map(version => {
          const newVersion = { ...version }
          const vStrength = this.getStrength(newVersion)
          if (vStrength) {
            newVersion.strength = vStrength
          }
          return newVersion
        }) || []
        if (strength) {
          newItem.strength = strength
        }
        newItem.disabled = (
          (selectOnOpen && item.id !== selectOnOpen.tree.id) ||
          (!selectOnOpen && this.answerValueSetList.some(vs => vs.ref === newItem.id))
        )
        return newItem
      })

      this.artefactSelectionModal.items = parsedItems
      this.artefactSelectionModal.title = selectOnOpen ? this.$t('edit-answer-valueset') : this.$t('select-valueset')
      this.artefactSelectionModal.disableDynamicVersions = selectOnOpen && selectOnOpen.version.flexibility !== 'dynamic'
      this.artefactSelectionModal.tree = this.treeValuesets
      this.artefactSelectionModal.versionProp = 'valueSet'
      this.artefactSelectionModal.addButtonType = selectOnOpen ? 'save' : 'add'
      if (selectOnOpen) {
        this.artefactSelectionModal.treeSelectOnOpen = selectOnOpen
        this.artefactSelectionModal.selected = this.$store.getters['terminology/getFullValueSetList']({ includeDynamic: true })?.filter(item => {
          return (
            item.id !== selectOnOpen.tree.id ||
            (
              (selectOnOpen.version.effectiveDate && item.effectiveDate !== selectOnOpen.version.effectiveDate) ||
              (!selectOnOpen.version.effectiveDate && selectOnOpen.version.flexibility && item.flexibility !== selectOnOpen.version.flexibility)
            )
          )
        })
      } else {
        this.artefactSelectionModal.selected = this.$store.getters['terminology/getFullValueSetList'](
          { includeDynamic: true }
        )?.filter(item => this.answerValueSetList.some(vs => vs.ref === item.id))
      }
      if (showCreateMenu) {
        this.artefactSelectionModal.titleAppend = ['createMenu']
      }
      this.artefactSelectionModal.detailsAppend = ['strength']
      this.artefactSelectionModal.selectHandler = (selectedItem) => {
        // Create an answerValueSet item in the correct format
        const formattedItem = {
          ref: selectedItem.id,
          flexibility: selectedItem.flexibility ?? selectedItem.effectiveDate,
          statusCode: selectedItem.statusCode,
          displayName: selectedItem.displayName,
          name: selectedItem.name,
          strength: selectedItem.strength
        }
        const list = cloneDeep(this.answerValueSetList) // Clone of the list including the added uuids
        const isUpdate = !!editUuid

        if (!Array.isArray(this.selectedItem.answerValueSet)) {
          this.$set(this.selectedItem, 'answerValueSet', [])
        }

        if (isUpdate) {
          list.splice(list.findIndex(vs => vs.uuid === editUuid), 1, formattedItem)
        } else {
          list.push(formattedItem)
        }

        list.forEach((vs, index) => {
          delete vs.uuid
        })
        this.selectedItem.answerValueSet = list
      }
      this.artefactSelectionModal.unselectHandler = () => {}
      this.artefactSelectionModal.open = true
    },
    async startEditMode () {
      try {
        const { effectiveDate, id: objectId } = this.questionnaireItem
        await LockService.lockObjectId({ effectiveDate, objectId })
        this.editMode = true
        this.fullScreen = true
      } catch (error) {
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
        this.$oops(errorMessage)
      }
    },
    startEditUnitOption (item, target = 'unitOption') {
      this.modals.editUnitOptions.selected = item
      this.modals.editUnitOptions.target = target
      this.modals.editUnitOptions.open = true
    },
    async startTerminologyBrowser () {
      if (this.hasUnsavedChanges) {
        const agree = await this.$confirm(this.$t('unsaved-data'), { positive: this.$t('save-and-continue'), zIndex: 99999 })
        if (!agree) return
        const closeAfterSave = false
        await this.saveQuestionnaire(closeAfterSave)
      }

      return this.$store.dispatch('dataset/changeActiveAssociationType', 'questionnaire')
        .then(async () => {
          await this.$store.dispatch('terminology/setStartShopping', true)
          this.$router.push({
            name: 'browser-project',
            params: {
              questionnairePanel: {
                questionnaireActive: this.questionnaireActive,
                selectedItem: this.selectedItem,
                questionnaireItem: this.questionnaireItem
              }
            }
          }).catch(() => {})
        })
    },
    startAddNewValueset () {
      const { name, params } = this.$route
      this.$store.commit('terminology/setCreateValuesetFrom', { name, params })
      this.$store.commit('terminology/setStartShopping', true)

      this.$router.push({ name: 'browser-project' }).catch(() => {})
    },
    tructateText (text, length = 100) {
      const truncated = text.substr(0, length)
      return text.length > truncated.length
        ? `${truncated}...`
        : text
    }
  },
  watch: {
    questionnaireContent: {
      immediate: true,
      deep: true,
      handler (content) {
        this.restoreEditableContent()
      }
    },
    watchDataForValueSetFilters: {
      immediate: true,
      deep: true,
      handler () {
        const defaultActive = ['new', 'draft', 'pending', 'final', 'undefined']
        this.artefactSelectionModal.filters = this.getItemStatuses?.map(status => {
          const currentlyActive = this.filters?.find(filter => filter.key === status.value)?.active
          return {
            name: this.$filters.getMatchingTranslation({ value: status.label, locale: this.$i18n.locale }),
            key: status.value,
            active: currentlyActive !== undefined
              ? currentlyActive
              : defaultActive.includes(status.value)
          }
        }) || ['']
        const referenceFilterActive = this.artefactSelectionModal.filters.find(filter => filter.key === undefined)?.active
        this.artefactSelectionModal.filters.push({
          name: this.$t('none'),
          key: undefined,
          active: referenceFilterActive !== undefined
            ? referenceFilterActive
            : defaultActive.includes('undefined')
        })
      }
    },
    '$route.params': {
      immediate: true,
      handler (params) {
        if (params.questionnairePanel?.selectedItem && Array.isArray(params.codesFromTerminologyBrowser) && !params.questionnairePanel.applyToOverallQuestionnaire) {
          this.selectedItem = this.$filters.listHelper.listHelper({
            items: this.editableContent,
            children: 'item',
            handler: match => params.questionnairePanel.selectedItem.linkId === match.linkId
          }).item
          const oldItems = this.selectedItem.code || []
          this.selectedItem.code = uniqBy([...oldItems, ...params.codesFromTerminologyBrowser], 'code')
          this.startEditMode()
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .fullScreen {
    transition: all 1s;
    z-index: 8;
    position: fixed;
    inset: 0;
    margin: 0 !important;
    & ::v-deep .v-data-table--fixed-height .v-data-table__wrapper  {
      overflow-y: hidden;
    }
  }

  .questionnaire-body-editor {
    & ::v-deep div.table-layer {
      min-width: min(600px, 30%);
    }

    & .answer-options-info-btn ::v-deep .editable-info-icon-button {
      top: 0px;
    }

    & ::v-deep div.active-item-layer .cardContent,
      ::v-deep div.table-layer {
        max-height: calc(100vh - 180px);
        overflow: auto;
    }

    & > div > div > div:not(.fullScreen) ::v-deep div.active-item-layer .cardContent {
      max-height: 600px;
    }
  }

  .cap-label {
    text-transform: capitalize;
  }
</style>
